import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'framer-motion';
import Logo from './Logo';
import { useAuthStore } from '../store/authStore';

export default function PublicNav() {
  const { user } = useAuthStore();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="fixed inset-x-0 top-0 z-50 bg-[#FAFAF9]/80 backdrop-blur-lg border-b border-gray-200/60">
      <nav className="mx-auto flex max-w-7xl 2xl:max-w-[1400px] 3xl:max-w-[1600px] items-center justify-between px-6 py-3 lg:px-8" aria-label="Global">
        <div className="flex lg:flex-1">
          <Link to="/" className="group">
            <Logo className="h-14 2xl:h-16 w-auto transition-opacity group-hover:opacity-80" />
          </Link>
        </div>

        {/* Desktop nav */}
        <div className="hidden sm:flex items-center gap-6 2xl:gap-8">
          <Link
            to="/features"
            className="text-sm 2xl:text-base font-medium text-slate-500 hover:text-navy-900 transition-colors"
          >
            Features
          </Link>
          <a
            href="/#how-it-works"
            className="text-sm 2xl:text-base font-medium text-slate-500 hover:text-navy-900 transition-colors"
          >
            How It Works
          </a>
          <Link
            to="/pricing"
            className="text-sm 2xl:text-base font-medium text-slate-500 hover:text-navy-900 transition-colors"
          >
            Pricing
          </Link>
          {user ? (
            <Link
              to="/dashboard"
              className="rounded-full bg-navy-900 px-5 py-2 2xl:px-6 2xl:py-2.5 text-sm 2xl:text-base font-medium text-white hover:bg-navy-800 transition-all"
            >
              Dashboard
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                className="text-sm 2xl:text-base font-medium text-slate-500 hover:text-navy-900 transition-colors"
              >
                Sign in
              </Link>
              <Link
                to="/request-demo"
                className="rounded-full bg-teal-600 px-5 py-2 2xl:px-6 2xl:py-2.5 text-sm 2xl:text-base font-medium text-white hover:bg-teal-500 transition-all"
              >
                Request a Demo
              </Link>
            </>
          )}
        </div>

        {/* Mobile hamburger button */}
        <button
          type="button"
          onClick={() => setMobileOpen(!mobileOpen)}
          className="sm:hidden p-2 -mr-2 text-slate-600 hover:text-navy-900 transition-colors"
          aria-label="Toggle menu"
        >
          {mobileOpen ? (
            <XMarkIcon className="h-6 w-6" />
          ) : (
            <Bars3Icon className="h-6 w-6" />
          )}
        </button>
      </nav>

      {/* Mobile dropdown */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="sm:hidden overflow-hidden border-t border-gray-200/60 bg-[#FAFAF9]/95 backdrop-blur-lg"
          >
            <div className="px-6 py-4 space-y-3">
              <Link
                to="/features"
                onClick={() => setMobileOpen(false)}
                className="block text-sm font-medium text-slate-600 hover:text-navy-900 transition-colors py-2"
              >
                Features
              </Link>
              <a
                href="/#how-it-works"
                onClick={() => setMobileOpen(false)}
                className="block text-sm font-medium text-slate-600 hover:text-navy-900 transition-colors py-2"
              >
                How It Works
              </a>
              <Link
                to="/pricing"
                onClick={() => setMobileOpen(false)}
                className="block text-sm font-medium text-slate-600 hover:text-navy-900 transition-colors py-2"
              >
                Pricing
              </Link>
              <div className="border-t border-gray-200/60 pt-3 space-y-3">
                {user ? (
                  <Link
                    to="/dashboard"
                    onClick={() => setMobileOpen(false)}
                    className="block w-full text-center rounded-full bg-navy-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-navy-800 transition-all"
                  >
                    Dashboard
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/login"
                      onClick={() => setMobileOpen(false)}
                      className="block text-sm font-medium text-slate-600 hover:text-navy-900 transition-colors py-2"
                    >
                      Sign in
                    </Link>
                    <Link
                      to="/request-demo"
                      onClick={() => setMobileOpen(false)}
                      className="block w-full text-center rounded-full bg-teal-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-teal-500 transition-all"
                    >
                      Request a Demo
                    </Link>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
